import React, { useEffect, useState, useRef, useCallback } from 'react'
import { useAuth } from '../hooks/auth'
import { config } from '../../config'
import { Notification } from './types'

export type UserBusMessage = {
    type: string
    body: Notification
}

export type UserBusContextType = {
    notificationsStream: Notification[]
    messagesStream: any[]
    isConnected: boolean
    error: string | null
    hideNotification: (notificationId: number) => void
    hideMessage: (messageId: number) => void
}

const UserBusContext = React.createContext<UserBusContextType>({
    notificationsStream: [],
    messagesStream: [],
    isConnected: false,
    error: null,
    hideNotification: () => {},
    hideMessage: () => {}
})

export const UserBusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { token } = useAuth()
    const [notificationsStream, setNotificationsStream] = useState<Notification[]>([])
    const [messagesStream, setMessagesStream] = useState<Notification[]>([])
    const [isConnected, setIsConnected] = useState(false)
    const [error, setError] = useState<string | null>(null)
    
    const websocketRef = useRef<WebSocket | null>(null)
    const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    const connectWebSocket = useCallback(() => {
        if (!token?.access) {
            setIsConnected(false)
            return
        }

        try {
            const url = `${config.api.urlSockets}/user_bus/?token=${token.access}`
            console.log('Connecting to user-bus WebSocket...', url)
            
            websocketRef.current = new WebSocket(url)

            websocketRef.current.onopen = () => {
                console.log('User-bus WebSocket connected')
                setIsConnected(true)
                setError(null)
            }

            websocketRef.current.onmessage = (event) => {
                try {
                    const message: UserBusMessage = JSON.parse(event.data)
                    console.log('Received user-bus message:', message)
                    onMessage(
                        message, 
                        (notification) => setNotificationsStream(prev => [...prev, notification]), 
                        (message) => setMessagesStream(prev => [...prev, message])
                    )
                } catch (parseError) {
                    console.error('Failed to parse user-bus message:', parseError)
                }
            }

            websocketRef.current.onerror = (event) => {
                console.error('User-bus WebSocket error:', event)
                setError('WebSocket connection error')
                setIsConnected(false)
            }

            websocketRef.current.onclose = (event) => {
                console.log('User-bus WebSocket closed:', event.code, event.reason)
                setIsConnected(false)
                
                // Attempt to reconnect after 5 seconds
                if (reconnectTimeoutRef.current) {
                    clearTimeout(reconnectTimeoutRef.current)
                }
                reconnectTimeoutRef.current = setTimeout(() => {
                    if (token?.access) {
                        connectWebSocket()
                    }
                }, 5000)
            }
        } catch (err) {
            console.error('Failed to create WebSocket connection:', err)
            setError('Failed to create WebSocket connection')
            setIsConnected(false)
        }
    }, [token?.access])

    const disconnectWebSocket = useCallback(() => {
        if (websocketRef.current) {
            websocketRef.current.close()
            websocketRef.current = null
        }
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current)
            reconnectTimeoutRef.current = null
        }
        setIsConnected(false)
    }, [])

    // Connect when token is available
    useEffect(() => {
        if (token?.access) {
            disconnectWebSocket()
            connectWebSocket()
        }
    }, [token?.access])

    const hideNotification = (notificationId: number) => {
        setNotificationsStream(prev => prev.filter(notification => notification.id !== notificationId))
    }

    const hideMessage = (messageId: number) => {
        setMessagesStream(prev => prev.filter(message => message.id !== messageId))
    }


    const contextValue: UserBusContextType = {
        notificationsStream,
        messagesStream,
        isConnected,
        error,
        hideNotification,
        hideMessage,
    }

    return (
        <UserBusContext.Provider value={contextValue}>
            {children}
        </UserBusContext.Provider>
    )
}

export const onMessage = (message: UserBusMessage, onNotification: (notification: Notification) => void, onMessage: (message: Notification) => void) => {
    if (message.type !== 'new_notification') {
        return
    }

    const notification = message.body

    if (['connect', 'gc_connect', 'following', 'book_review'].includes(notification.type)) {
        onNotification(notification)
    } else if (['cafe', 'salon', 'gc_cafe', 'gc_salon'].includes(notification.type)) {
        onMessage(notification)
    } else {
        console.warn('Unknown message type:', message.type)
    }
}

export const useUserBus = (): UserBusContextType => {
    const context = React.useContext(UserBusContext)
    if (context === undefined) {
        throw new Error('useUserBus must be used within a UserBusProvider')
    }
    return context
}
